import ctl from '@netlify/classnames-template-literals'
import { AnimatePresence, motion } from 'motion/react'
import { ReactNode } from 'react'
import { AnimateChangeInHeight } from '../../animation'
import { Tooltip } from '../../tooltip'

export interface MiniWidgetTabsProps<T extends string> {
	hidden?: boolean

	name?: ReactNode

	value: T
	onChange: (value: T) => void
	maxWidth?: number

	tabs: Array<{
		id: T
		icon: ReactNode
		label?: string
		children: ReactNode
	}>
}

export function MiniTabbedWidgetWrapper<T extends string>(
	{ hidden, value, onChange, tabs, maxWidth }: MiniWidgetTabsProps<T>,
) {
	if (hidden) { return null }

	return (
		<div
			className={ctl(`
				[contain:layout] text-xs bg-grey-50 ring ring-grey-100 text-grey-700 rounded-sm p-[3px] w-full shadow
			`)}
		>
			<div className="flex mx-[-1px] gap-1 items-center" role="tablist">
				{tabs.map((tab) => {
					const button = (
						<button
							key={tab.id}
							className={ctl(`
								rounded-t-sm py-1 px-1.5 border-t border-x border-grey-100 relative
								cursor-pointer h-[28px] w-[32px] grid place-items-center text-grey-700
								${tab.id === value && 'border-b-0 mb-[-1px] bg-white z-10'}
								${
								tab.id !== value
								&& 'border-b bg-gradient-to-b from-grey-100 to-grey-50 hover:text-grey-900 hover:to-white hover:from-bg-grey-50'
							}
							`)}
							onClick={() => onChange(tab.id)}
							role="tab"
							aria-selected={tab.id === value}
						>
							<div
								className={ctl(`
									absolute inset-x-0 bottom-0 h-2
									bg-gradient-to-b from-grey-200/0 to-grey-200/30
									${tab.id === value && 'opacity-0'}
									${tab.id !== value && 'opacity-100'}
								`)}
							/>
							{tab.icon}
						</button>
					)

					return tab.label
						? (
							<Tooltip key={tab.id as string} content={tab.label}>
								{button}
							</Tooltip>
						)
						: button
				})}
			</div>
			<div
				className="bg-white rounded-b-sm rounded-tr-sm ring ring-grey-100 overflow-hidden relative"
				role="tabpanel"
			>
				<AnimateChangeInHeight duration={0.2}>
					<div>
						<AnimatePresence mode="popLayout">
							<motion.div
								key={value}
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.2 }}
								className="overflow-auto max-h-[80vh] p-2"
								style={{ maxWidth: maxWidth }}
							>
								{tabs.find((tab) => tab.id === value)?.children}
							</motion.div>
						</AnimatePresence>
					</div>
				</AnimateChangeInHeight>
			</div>
		</div>
	)
}
